import { Group, Text } from '@mantine/core';

/**
 * Marca visual de decaPlus: logo (PNG renderizado como máscara CSS para
 * poder colorearlo) + wordmark `decaPlus` con `Plus` destacado.
 *
 * El PNG `/deca-logo.png` viene de la landing (deca-landing/public) y es
 * una silueta blanca sobre fondo transparente. Si lo metieras con <img>
 * directo, sería invisible sobre fondo blanco. La técnica de `mask` +
 * `background` permite pintarlo del color de marca sin tener un SVG.
 *
 * Se usa en:
 *  - Login / Signup (variante `lg`, en card centrado sobre bg blanco).
 *  - AppShell header (variante `sm`, cabe en los 60px del header).
 */
type BrandSize = 'sm' | 'md' | 'lg';

const SIZE_MAP: Record<BrandSize, { mark: number; font: number }> = {
  sm: { mark: 24, font: 18 },
  md: { mark: 32, font: 22 },
  lg: { mark: 40, font: 28 },
};

export function BrandMark({ size = 40 }: { size?: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        background: 'var(--mantine-color-deca-6)',
        mask: 'url(/deca-logo.png) center / contain no-repeat',
        WebkitMask: 'url(/deca-logo.png) center / contain no-repeat',
      }}
    />
  );
}

export function Brand({ size = 'lg' }: { size?: BrandSize }) {
  const { mark, font } = SIZE_MAP[size];
  return (
    <Group gap="sm" align="center" wrap="nowrap">
      <BrandMark size={mark} />
      <Text fw={800} fz={font} lh={1} c="deca.9" component="span">
        deca
        <Text component="span" inherit fw={800} fz={font} c="deca.6">
          Plus
        </Text>
      </Text>
    </Group>
  );
}
